import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { PaymentSchedule } from '@app/domain/model/eshop/life/PaymentSchedule';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { Bank } from '@app/domain/model/Bank';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { determineLifePackageTitle } from '@app/infrastructure/eshop/transport/determineLifePackageTitle';
import { LifePackageCode } from '@app/domain/model/eshop/life/LifePackageCode';
import { determineCoverageName } from '@app/infrastructure/policies/determineCoverageName';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseLifeInsuranceResult } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { format, addYears } from 'date-fns';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { PaymentScheduleDetailsView } from '@app/app/esales/life/_sections/steps/PaymentScheduleDetailsView';

interface Props {
    formState: FormState<PurchaseLifeInsuranceResult>;
    handleEditContractDuration: () => void;
    handleEditPaymentSchedule: () => void;
    handleEditDefenses: () => void;
}

export function ContractInformationCard({
    formState,
    handleEditContractDuration,
    handleEditPaymentSchedule,
    handleEditDefenses,
}: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();
    const parameters = useAppParameters();

    const [selectedScheduleForDetails, setSelectedScheduleForDetails] = useState<Nullable<string>>(null);
    const [isDetailsViewOpen, setIsDetailsViewOpen] = useState(false);

    const contractStartDate = useMemo(() => {
        return format(new Date(), 'dd.MM.yyyy');
    }, []);

    const contractEndDate = useMemo(() => {
        const insuranceDurationYears = formState.data?.formData.insuranceDurationYears ?? 0;
        const endDate = addYears(new Date(), insuranceDurationYears);
        return format(endDate, 'dd.MM.yyyy');
    }, [formState.data?.formData.insuranceDurationYears]);

    const covers = useMemo(() => {
        const selectedPackage = formState.data?.formData.selectedPackage;

        if (selectedPackage === LifePackageCode.Free) {
            return formState.data?.formData.selectedCustomCovers ?? [];
        }

        const packageData = formState.data?.packages?.find(pkg => pkg.code === selectedPackage);
        return (packageData?.covers ?? []).map(cover => ({
            code: cover.code,
            sum: cover.default,
        }));
    }, [
        formState.data?.formData.selectedPackage,
        formState.data?.formData.selectedCustomCovers,
        formState.data?.packages,
    ]);

    const getPaymentScheduleLabel = useCallback(
        (schedule: string) => {
            switch (schedule) {
                case PaymentSchedule.Changing:
                    return t('eshop.life.changingPayment');
                case PaymentSchedule.Increasing:
                    return t('eshop.life.increasingPayment');
                case PaymentSchedule.Fixed:
                    return t('eshop.life.fixedPayment');
                default:
                    return schedule;
            }
        },
        [t],
    );

    const getBankName = useCallback(
        (bankCode: string) => {
            switch (bankCode) {
                case Bank.Swedbank:
                    return t('eshop.life.banks.swedbank');
                case Bank.Seb:
                    return t('eshop.life.banks.seb');
                case Bank.Luminor:
                    return t('eshop.life.banks.luminor');
                case Bank.NoEInvoice:
                    return t('eshop.life.banks.noEInvoice');
                default:
                    return bankCode;
            }
        },
        [t],
    );

    const handlePaymentScheduleOpen = useCallback(() => {
        setSelectedScheduleForDetails(formState.data?.formData.paymentSchedule ?? null);
        setIsDetailsViewOpen(true);
    }, [formState.data?.formData.paymentSchedule]);

    const handleCloseDetailsView = useCallback(() => {
        setIsDetailsViewOpen(false);
        setSelectedScheduleForDetails(null);
    }, []);

    const conditionsHref = useMemo(() => {
        if (formState.data?.formData.paymentSchedule === PaymentSchedule.Fixed) {
            return parameters.eshopLifeInfoUrls.fixedPaymentConditions;
        } else {
            return parameters.eshopLifeInfoUrls.increasingPaymentConditions;
        }
    }, [
        formState.data?.formData.paymentSchedule,
        parameters.eshopLifeInfoUrls.fixedPaymentConditions,
        parameters.eshopLifeInfoUrls.increasingPaymentConditions,
    ]);

    return (
        <>
            <CollapsableCard defaultCollapsed={false} className="p-6" label={t('eshop.life.contractInformation')}>
                <CollapsablePanel
                    label={t('eshop.life.contractDuration')}
                    addon={
                        <TextButton color="primary" preventDefault onClick={handleEditContractDuration}>
                            {t('eshop.life.iWillChange')}
                        </TextButton>
                    }
                    collapsable={false}>
                    <div className="grid w-full grid-cols-2 gap-4 py-4">
                        <ReadOnlyField className="col-span-2" label={t('eshop.life.insurancePeriod')}>
                            {formState.data?.formData.insuranceDurationYears} {t('general.units.years')}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.life.theBeginning')}>{contractStartDate}</ReadOnlyField>
                        <ReadOnlyField label={t('eshop.life.theEnd')}>{contractEndDate}</ReadOnlyField>
                    </div>
                </CollapsablePanel>
                <CollapsablePanel
                    label={t('eshop.life.payments')}
                    addon={
                        <TextButton color="primary" preventDefault onClick={handleEditPaymentSchedule}>
                            {t('eshop.life.iWillChange')}
                        </TextButton>
                    }
                    collapsable={false}>
                    <div className="grid w-full grid-cols-1 gap-4 py-4">
                        <ReadOnlyField label={t('eshop.life.contractType')}>
                            {getPaymentScheduleLabel(formState.data?.formData.paymentSchedule ?? '')}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.life.monthlyPaymentOverview')}>
                            {formatEuroValue(formState.data?.calculation?.premium ?? 0, tenant)}
                        </ReadOnlyField>
                        <ReadOnlyField
                            label={t(
                                'eshop.life.theEInvoiceGoes',
                            )}>{`${formState.data?.formData.currentAccountNumber} ${getBankName(formState.data?.formData.bank ?? '')}`}</ReadOnlyField>
                        <TextButton
                            rightAddon={<ArrowRightIcon className="size-5" />}
                            className="underline underline-offset-2"
                            onClick={handlePaymentScheduleOpen}
                            preventDefault>
                            {t('eshop.life.paymentSchedule')}
                        </TextButton>
                    </div>
                </CollapsablePanel>
                <CollapsablePanel
                    label={t('eshop.life.insuranceProtections')}
                    addon={
                        <TextButton color="primary" preventDefault onClick={handleEditDefenses}>
                            {t('eshop.life.iWillChange')}
                        </TextButton>
                    }
                    collapsable={false}>
                    <div className="grid w-full grid-cols-1 gap-4 py-4">
                        <ReadOnlyField label={t('eshop.life.aPackage')}>
                            {formState.data?.formData.selectedPackage
                                ? t(determineLifePackageTitle(formState.data.formData.selectedPackage))
                                : '-'}
                        </ReadOnlyField>
                        {covers
                            .filter(cover => determineCoverageName(cover.code) !== '')
                            .map(cover => (
                                <ReadOnlyField key={cover.code} label={t(determineCoverageName(cover.code))}>
                                    {formatEuroValue(cover.sum, tenant, true)}
                                </ReadOnlyField>
                            ))}
                        <TextButton
                            rightAddon={<ArrowRightIcon className="size-5" />}
                            href={conditionsHref}
                            className="underline underline-offset-2"
                            target="_blank"
                            preventDefault>
                            {t('eshop.life.conditions')}
                        </TextButton>
                    </div>
                </CollapsablePanel>
            </CollapsableCard>
            <PaymentScheduleDetailsView
                paymentSchedule={selectedScheduleForDetails}
                calculation={formState.data?.calculation ?? null}
                open={isDetailsViewOpen}
                onClose={handleCloseDetailsView}
            />
        </>
    );
}
